import { useMemo } from 'react'
import { type Column } from '@tanstack/react-table'
import { Check, CirclePlus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'

export function selectedFacetFilterValues(filterValue: unknown): Set<string> {
  if (Array.isArray(filterValue)) {
    return new Set(filterValue.map((entry) => String(entry)))
  }
  if (typeof filterValue === 'string' && filterValue.trim() !== '') {
    return new Set(
      filterValue
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
    )
  }
  return new Set()
}

export type FacetedFilterOption =
  | {
      type?: 'option'
      label: string
      value: string
      icon?: React.ComponentType<{ className?: string }>
    }
  | { type: 'separator' }

type DataTableFacetedFilterProps<TData, TValue> = {
  column?: Column<TData, TValue>
  title?: string
  /** When false, option row counts from faceted unique values are hidden. Default true. */
  showCounts?: boolean
  options: FacetedFilterOption[]
}

function isFacetedFilterOption(
  option: FacetedFilterOption
): option is Extract<FacetedFilterOption, { type?: 'option' }> {
  return option.type !== 'separator'
}

export function DataTableFacetedFilter<TData, TValue>({
  column,
  title,
  showCounts = true,
  options,
}: DataTableFacetedFilterProps<TData, TValue>) {
  const { t } = useTranslation('common')
  const facets = useMemo(() => {
    if (!showCounts || !column) {
      return new Map<string, number>()
    }
    try {
      return column.getFacetedUniqueValues() ?? new Map<string, number>()
    } catch {
      return new Map<string, number>()
    }
  }, [column, showCounts])
  const selectedValues = selectedFacetFilterValues(column?.getFilterValue())

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant='outline' size='sm' className='h-8 border-dashed'>
          <CirclePlus className='size-4' />
          {title}
          {selectedValues.size > 0 && (
            <span
              className='ms-1.5 size-1.5 shrink-0 rounded-full bg-primary'
              aria-hidden
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className='w-[200px] p-0' align='start'>
        <Command>
          <CommandInput placeholder={title} />
          <CommandList>
            <CommandEmpty>{t('dataTable.noResults')}</CommandEmpty>
            <CommandGroup>
              {options.map((option, index) => {
                if (!isFacetedFilterOption(option)) {
                  return <CommandSeparator key={`separator-${index}`} />
                }
                const isSelected = selectedValues.has(option.value)
                return (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    keywords={[option.label]}
                    onSelect={() => {
                      if (isSelected) {
                        selectedValues.delete(option.value)
                      } else {
                        selectedValues.add(option.value)
                      }
                      const filterValues = Array.from(selectedValues)
                      column?.setFilterValue(
                        filterValues.length ? filterValues : undefined
                      )
                    }}
                  >
                    <div
                      className={cn(
                        'flex size-4 items-center justify-center rounded-sm border border-primary',
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'opacity-50 [&_svg]:invisible'
                      )}
                    >
                      <Check className={cn('h-4 w-4 text-background')} />
                    </div>
                    {option.icon && (
                      <option.icon className='size-4 text-muted-foreground' />
                    )}
                    <span>{option.label}</span>
                    {showCounts && facets.has(option.value) && (
                      <span className='ms-auto flex h-4 w-4 items-center justify-center font-mono text-xs'>
                        {facets.get(option.value)}
                      </span>
                    )}
                  </CommandItem>
                )
              })}
            </CommandGroup>
            {selectedValues.size > 0 && (
              <>
                <CommandSeparator />
                <CommandGroup>
                  <CommandItem
                    onSelect={() => column?.setFilterValue(undefined)}
                    className='justify-center text-center'
                  >
                    {t('dataTable.clearFilters')}
                  </CommandItem>
                </CommandGroup>
              </>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
